import { Box, Stack, Tooltip } from '@mui/material'
import { useMinimalsTheme } from '@tom-ui/ui'
import { AllocationRuleDto, ConfigAreaType } from '@yard/app/api'
import {
  blockedBackgroundPattern,
  computeSlotStyle,
  getSlotSpan,
  mergeSlotRuleDataForSpan,
  precomputeSlotRuleMap,
  resolveSlotSchemeColor,
} from '@yard/utils/occupancies.utils'
import { getEffectiveMaxTiers } from '@yard/utils/yard-block.util'
import { memo, useMemo, useState } from 'react'
import { ColorScheme } from '../../stores/yard.view-store'
import {
  BayGap,
  ROW_HEIGHT,
  baySpanExtraGridColumns,
  buildGridTemplateColumns,
  edgeAt,
  generateSlotKey,
} from '../../helpers/yard-view.utils'
import { OccupancyWithFilter } from '../../models/yard-view.model'

import { BayGapCell } from './BayGapCell'
import { MaxStackHeight } from './MaxStackHeight'
import { YardBlockSlotsContextMenu } from './YardBlockSlotsContextMenu'
import { YardSlotTooltipContent } from './YardSlotTooltipContent'

import { StackConfigArea } from '../../models/yard-view.model'

interface Props {
  allocationRules: AllocationRuleDto[]
  selectedAllocationRuleIndex?: number
  showAllocationRulesPreview: boolean
  blockId: string
  isEditable: boolean
  maxBays: number
  maxRows: number
  maxTiers: number
  occupancies: Record<string, OccupancyWithFilter[]>
  colorScheme?: ColorScheme
  selectedSchemeValues: ReadonlyArray<string>
  stackConfigArea: Record<string, StackConfigArea>
  onClick: (bay: number, row: number) => void
  onBlockConfigArea: (minBay: number, maxBay: number, minRow: number, maxRow: number) => void
  onDeleteConfigArea: (
    configAreaType: ConfigAreaType,
    minBay: number,
    maxBay: number,
    minRow: number,
    maxRow: number,
  ) => void
  onAdjustMaxTier: (bay: number, row: number) => void
  bayGaps: BayGap[]
  hasLeadingGap?: boolean
}

const slotBaseSx = {
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'background-color 150ms ease, box-shadow 150ms ease',
} as const

export const YardBlockSlots = memo(function YardBlockSlots(props: Props) {
  const {
    blockId,
    allocationRules,
    selectedAllocationRuleIndex,
    showAllocationRulesPreview,
    isEditable,
    maxBays,
    maxRows,
    occupancies,
    colorScheme,
    selectedSchemeValues,
    onClick,
    onBlockConfigArea,
    onDeleteConfigArea,
    onAdjustMaxTier,
    bayGaps,
    hasLeadingGap,
  } = props
  const theme = useMinimalsTheme()

  const [contextMenu, setContextMenu] = useState<{
    mouseX: number
    mouseY: number
    bayIdx: number
    rowIdx: number
  } | null>(null)

  const [highlightedSlot, setHighlightedSlot] = useState<string | null>(null)

  const slotConfigForContext = useMemo(() => {
    if (!contextMenu) return null
    const slotKey = generateSlotKey(contextMenu.bayIdx, contextMenu.rowIdx)

    return props.stackConfigArea[slotKey]
  }, [contextMenu, props.stackConfigArea])

  const slotRuleMap = useMemo(
    () =>
      precomputeSlotRuleMap(
        blockId,
        maxBays,
        maxRows,
        allocationRules,
        selectedAllocationRuleIndex,
      ),
    [blockId, maxBays, maxRows, allocationRules, selectedAllocationRuleIndex],
  )

  const selectedSchemeValuesSet = useMemo(
    () => new Set(selectedSchemeValues),
    [selectedSchemeValues],
  )

  const cells: React.ReactNode[] = []

  for (let rowIdx = 1; rowIdx <= maxRows; rowIdx++) {
    const leadingGap = edgeAt(bayGaps, 0)
    if (hasLeadingGap && leadingGap) {
      cells.push(
        <BayGapCell
          key={`gap-${rowIdx}-leading`}
          gap={leadingGap}
          variant='plain'
          sx={{ height: ROW_HEIGHT }}
        />,
      )
    }

    let bayIdx = 1

    while (bayIdx <= maxBays) {
      const key = generateSlotKey(bayIdx, rowIdx)
      const slotOccupancies = occupancies[key] || []

      const currentBayIdx = bayIdx //since the variables are changing in the loop, we need to capture the value here
      const currentRowIdx = rowIdx

      const span = getSlotSpan({
        bayIdx,
        rowIdx,
        maxBays,
        occupancies,
      })

      const slotKey = generateSlotKey(currentBayIdx, currentRowIdx)
      const slotConfig = props.stackConfigArea[slotKey]
      const effectiveMaxTiers = getEffectiveMaxTiers(props.maxTiers, slotConfig)

      const mergedRuleData = mergeSlotRuleDataForSpan(
        slotRuleMap,
        currentBayIdx,
        currentRowIdx,
        span,
      )

      const schemeBackgroundColor = resolveSlotSchemeColor(
        slotOccupancies,
        colorScheme,
        selectedSchemeValuesSet,
      )

      const slotStyle = computeSlotStyle(
        showAllocationRulesPreview,
        !!highlightedSlot && highlightedSlot === key,
        slotOccupancies.some(occ => occ.isFiltered),
        mergedRuleData,
        theme.palette.text.primary,
        slotOccupancies.length,
        effectiveMaxTiers,
        schemeBackgroundColor,
      )

      const gridSpan = span + baySpanExtraGridColumns(bayIdx, span, bayGaps)
      const cellKey = `row-${rowIdx}-bay-${bayIdx}`

      const slotStack = (
        <Stack
          sx={{
            ...slotBaseSx,
            borderWidth: slotStyle.borderWidth,
            borderStyle: slotStyle.borderStyle,
            borderColor: slotStyle.borderColor,
            boxShadow: slotStyle.boxShadow,
            gap: theme.customSpacing.xxxs,
            background: slotConfig?.isBlocked
              ? blockedBackgroundPattern(slotStyle.blockedPatternColor)
              : undefined,
            backgroundColor: slotStyle.backgroundColor,
            gridColumn: `span ${gridSpan}`,
            ...(slotStyle.filterOverlay && {
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                backgroundColor: slotStyle.filterOverlay,
                pointerEvents: 'none',
                zIndex: 0,
              },
            }),
          }}
          onDoubleClick={() => onClick(currentBayIdx, currentRowIdx)}
          onContextMenu={e => {
            if (!isEditable) return
            e.preventDefault()
            setHighlightedSlot(key)
            setContextMenu({
              mouseX: e.clientX,
              mouseY: e.clientY,
              bayIdx: currentBayIdx,
              rowIdx: currentRowIdx,
            })
          }}
        >
          <MaxStackHeight
            maxStackHeight={slotConfig?.slotMaxStackHeight}
            sx={{
              position: 'absolute',
              right: theme.customSpacing.xxxs,
              color: slotStyle.textColor,
              scale: '0.7',
              zIndex: 1,
            }}
          />
        </Stack>
      )

      // Always render through a Tooltip wrapper so the grid layout is stable
      // (no remount when a slot transitions from empty to occupied). For empty
      // slots we suppress every Tooltip listener so dense yards don't pay for
      // ~thousands of dormant hover handlers (per L2.4 perf subtask).
      const isOccupied = slotOccupancies.length > 0
      cells.push(
        <Tooltip
          key={cellKey}
          title={isOccupied ? <YardSlotTooltipContent occupancies={slotOccupancies} /> : ''}
          enterDelay={400}
          leaveDelay={100}
          placement='top'
          disableHoverListener={!isOccupied}
          disableFocusListener={!isOccupied}
          disableTouchListener={!isOccupied}
        >
          {slotStack}
        </Tooltip>,
      )

      const lastBayInSpan = bayIdx + span - 1
      const trailingGap = edgeAt(bayGaps, lastBayInSpan)
      if (trailingGap) {
        cells.push(
          <BayGapCell
            key={`gap-${rowIdx}-after-bay-${lastBayInSpan}`}
            gap={trailingGap}
            variant='plain'
            sx={{ height: ROW_HEIGHT }}
          />,
        )
      }

      bayIdx += span
    }
  }

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: buildGridTemplateColumns(maxBays, bayGaps),
          gridTemplateRows: `repeat(${maxRows}, ${ROW_HEIGHT}px)`,
        }}
      >
        {cells}
      </Box>

      <YardBlockSlotsContextMenu
        open={!!contextMenu}
        anchorPosition={
          contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
        }
        onBlockConfigArea={
          contextMenu !== null && !slotConfigForContext?.isBlocked
            ? () =>
                onBlockConfigArea(
                  contextMenu.bayIdx,
                  contextMenu.bayIdx,
                  contextMenu.rowIdx,
                  contextMenu.rowIdx,
                )
            : undefined
        }
        onDeleteConfigArea={
          contextMenu !== null && slotConfigForContext?.isBlocked
            ? configAreaType =>
                onDeleteConfigArea(
                  configAreaType,
                  contextMenu.bayIdx,
                  contextMenu.bayIdx,
                  contextMenu.rowIdx,
                  contextMenu.rowIdx,
                )
            : undefined
        }
        onAdjustMaxTier={() => {
          if (!contextMenu) return
          onAdjustMaxTier(contextMenu.bayIdx, contextMenu.rowIdx)
        }}
        onClose={() => {
          setContextMenu(null)
          setTimeout(() => {
            setHighlightedSlot(null)
          }, 100)
        }}
      />
    </>
  )
})
